if (!customElements.get('main-nav')) {
	class MainNav extends HTMLElement {
		constructor() {
			super();
			this.openDelay = 70;
			this.closeDelay = 160;
			this.current = null;
			this.openTimer = null;
			this.closeTimer = null;
			this.frame = null;
			this.onReposition = this.onReposition.bind(this);
		}

		connectedCallback() {
			this.items = Array.from(this.querySelectorAll('.main-nav__item--parent'));
			this.backdrop = this.querySelector('[data-nav-backdrop]');
			this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

			if (!this.items.length) {
				return;
			}

			this.items.forEach((item) => {
				item.addEventListener('mouseenter', () => this.onItemEnter(item));
				item.addEventListener('mouseleave', () => this.scheduleClose());
				item.addEventListener('focusin', () => this.open(item));
			});

			if (this.backdrop) {
				this.backdrop.addEventListener('mouseenter', () => this.close());
				this.backdrop.addEventListener('click', () => this.close());
			}

			this.addEventListener('focusout', (event) => {
				if (!this.contains(event.relatedTarget)) {
					this.close();
				}
			});

			document.addEventListener('keydown', (event) => {
				if (event.key !== 'Escape' || !this.current) {
					return;
				}
				const link = this.current.querySelector('.main-nav__link');
				this.close();
				if (link) {
					link.focus();
				}
			});
		}

		disconnectedCallback() {
			this.close();
		}

		onItemEnter(item) {
			if (!this.hoverable.matches) {
				return;
			}
			clearTimeout(this.openTimer);
			clearTimeout(this.closeTimer);

			if (this.current) {
				this.open(item);
				return;
			}
			this.openTimer = setTimeout(() => this.open(item), this.openDelay);
		}

		scheduleClose() {
			clearTimeout(this.openTimer);
			clearTimeout(this.closeTimer);
			this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
		}

		open(item) {
			clearTimeout(this.openTimer);
			clearTimeout(this.closeTimer);

			if (this.current === item) {
				return;
			}
			if (this.current) {
				this.deactivate(this.current);
			}

			this.current = item;
			item.classList.add('is-open');

			const link = item.querySelector('.main-nav__link');
			if (link) {
				link.setAttribute('aria-expanded', 'true');
			}

			this.positionPanel(item.querySelector('[data-nav-panel]'));
			this.setBackdropOffset();
			this.classList.add('is-open');

			window.addEventListener('scroll', this.onReposition, { passive: true });
			window.addEventListener('resize', this.onReposition);
		}

		close() {
			clearTimeout(this.openTimer);
			clearTimeout(this.closeTimer);

			if (this.current) {
				this.deactivate(this.current);
				this.current = null;
			}

			this.classList.remove('is-open');
			window.removeEventListener('scroll', this.onReposition);
			window.removeEventListener('resize', this.onReposition);
		}

		deactivate(item) {
			item.classList.remove('is-open');
			const link = item.querySelector('.main-nav__link');
			if (link) {
				link.setAttribute('aria-expanded', 'false');
			}
		}

		positionPanel(panel) {
			if (!panel) {
				return;
			}

			const gutter = 16;
			panel.style.marginLeft = '';

			const rect = panel.getBoundingClientRect();
			let shift = 0;

			if (rect.right > window.innerWidth - gutter) {
				shift = window.innerWidth - gutter - rect.right;
			}
			if (rect.left + shift < gutter) {
				shift = gutter - rect.left;
			}
			if (shift) {
				panel.style.marginLeft = `${Math.round(shift)}px`;
			}
		}

		setBackdropOffset() {
			const host = this.closest('.header-section') || this.closest('.header') || this;
			const bottom = Math.max(0, Math.round(host.getBoundingClientRect().bottom));
			this.style.setProperty('--nav-backdrop-top', `${bottom}px`);
		}

		onReposition() {
			if (this.frame) {
				return;
			}
			this.frame = requestAnimationFrame(() => {
				this.frame = null;
				if (!this.current) {
					return;
				}
				this.setBackdropOffset();
				this.positionPanel(this.current.querySelector('[data-nav-panel]'));
			});
		}
	}

	customElements.define('main-nav', MainNav);
}

if (!customElements.get('mobile-nav')) {
	class MobileNav extends HTMLElement {
		connectedCallback() {
			this.panels = new Map();
			this.stack = [];

			this.querySelectorAll('[data-nav-panel]').forEach((panel) => {
				this.panels.set(panel.getAttribute('data-nav-panel'), panel);
			});

			this.root = this.panels.get('root');
			if (!this.root) {
				return;
			}

			this.querySelectorAll('[data-nav-to]').forEach((button) => {
				button.addEventListener('click', (event) => {
					event.preventDefault();
					this.push(button.getAttribute('data-nav-to'));
				});
			});

			this.querySelectorAll('[data-nav-back]').forEach((button) => {
				button.addEventListener('click', (event) => {
					event.preventDefault();
					this.pop();
				});
			});

			this.addEventListener('mobile-menu:close', () => this.reset());
		}

		currentPanel() {
			if (!this.stack.length) {
				return this.root;
			}
			return this.panels.get(this.stack[this.stack.length - 1]);
		}

		push(id) {
			const next = this.panels.get(id);
			const current = this.currentPanel();

			if (!next || next === current) {
				return;
			}

			current.classList.remove('is-active');
			current.classList.add('is-previous');
			next.classList.remove('is-previous');
			next.classList.add('is-active');
			next.scrollTop = 0;
			this.stack.push(id);

			const back = next.querySelector('[data-nav-back]');
			if (back) {
				back.focus({ preventScroll: true });
			}
		}

		pop() {
			if (!this.stack.length) {
				return;
			}

			const panel = this.panels.get(this.stack.pop());
			if (panel) {
				panel.classList.remove('is-active', 'is-previous');
			}

			const previous = this.currentPanel();
			previous.classList.remove('is-previous');
			previous.classList.add('is-active');
		}

		reset() {
			this.stack = [];
			this.panels.forEach((panel) => panel.classList.remove('is-active', 'is-previous'));
			this.root.classList.add('is-active');
			this.root.scrollTop = 0;
		}
	}

	customElements.define('mobile-nav', MobileNav);
}
